import { Drawer, Spinner, Text, VStack, Portal, CloseButton } from '@chakra-ui/react';
import { useFilterPreview } from '../../hooks/useContactList'
import { useSavedSearchPreview } from '../../hooks/useSavedSearch'
import { extractApiMessage } from '../../lib/apiError'
import { useIsMobile } from '../../hooks/useIsMobile'
import { ResponsiveTable, type ResponsiveColumn } from '../shared'
import type { ContactPreview, FilterEvaluationRequest } from '../../types/contactList'
import type { FilterSource } from './LiveCountBadge'

const columns: ResponsiveColumn<ContactPreview>[] = [
  { header: 'Name', cell: (c) => c.fullName, mobileRole: 'title' },
  { header: 'City', cell: (c) => c.city ?? '-', mobileRole: 'meta' },
  { header: 'Zip', cell: (c) => c.zip ?? '-', mobileRole: 'meta' },
  { header: 'Party', cell: (c) => c.party ?? '-', mobileRole: 'badge' },
  { header: 'Age', cell: (c) => c.age ?? '-', mobileRole: 'meta', isNumeric: true },
]

interface PreviewDrawerProps {
  isOpen: boolean
  onClose: () => void
  request: FilterEvaluationRequest | null
  source?: FilterSource
}

export function PreviewDrawer({ isOpen, onClose, request, source = 'lists' }: PreviewDrawerProps) {
  const isMobile = useIsMobile()
  const listResult = useFilterPreview(request, source === 'lists' && isOpen && !!request)
  const savedResult = useSavedSearchPreview(request, source === 'saved-searches' && isOpen && !!request)
  const { data, isLoading, error } = source === 'saved-searches' ? savedResult : listResult

  return (
    <Drawer.Root open={isOpen} placement={isMobile ? 'bottom' : 'end'} size={isMobile ? 'full' : 'lg'} onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px">
              Preview Results (first 25)
            </Drawer.Header>
            <Drawer.Body p={0}>
              {!request && (
                <VStack py={12} gap={3}>
                  <Text color="gray.500">Add filters to preview results.</Text>
                </VStack>
              )}

              {request && isLoading && (
                <VStack py={12} gap={3}>
                  <Spinner size="lg" color="accent" />
                  <Text color="gray.500">Loading preview...</Text>
                </VStack>
              )}

              {!!error && (
                <VStack py={12} gap={3}>
                  <Text color="red.500" fontWeight="medium">Error loading preview</Text>
                  <Text color="gray.500" fontSize="sm">
                    {extractApiMessage(error, 'Check browser console for details.')}
                  </Text>
                </VStack>
              )}

              {request && !isLoading && !error && data && data.length === 0 && (
                <VStack py={12} gap={3}>
                  <Text color="gray.500">No contacts match these filters.</Text>
                </VStack>
              )}

              {request && !isLoading && data && data.length > 0 && (
                <ResponsiveTable columns={columns} rows={data} keyFn={(c) => c.id} bare />
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>

      </Portal>
    </Drawer.Root>
  );
}
